import { useState, useEffect, useMemo } from "react";
import { anatomyConstants } from "../../../constants/anatomyConstants";

const CenterlineMeshSelectionModal = ({
  isMobile,
  modelRef,
  onMoveToStructure,
  onCenterline,
  onOpacityChange,
  onVisibilityChange,
}) => {
  const [selectedIdxInList, setSelectedIdxInList] = useState(null);
  const [visibility, setVisibility] = useState([]);
  const [opacity, setOpacity] = useState([]);

  // All scene children
  const children = useMemo(() => {
    return modelRef?.current?.children || [];
  }, [modelRef]);

  // Build a list that REMEMBERS each child's original index,
  // then filter by isWatertight and sort by renderOrder (desc here).
  const list = useMemo(() => {
    return children
      .map((mesh, originalIndex) => ({ mesh, originalIndex }))
      .filter(({ mesh }) => mesh?.isWatertight) // only watertight
      .sort((a, b) => b.mesh.renderOrder - a.mesh.renderOrder); // highest first
  }, [children]);

  useEffect(() => {
    setVisibility(children.map((m) => m.visible));
    setOpacity(children.map((m) => m.material.opacity));
  }, [children]);

  // Helpers use ORIGINAL index in the scene children array
  const handleCenterline = (originalIndex) => {
    if (onCenterline) {
      const fileName = children[originalIndex]?.name;
      onCenterline(fileName);
    }
    moveToStructure(originalIndex);
    makeOthersInvisible(originalIndex);
    handleOpacityChange(originalIndex, 0.5);
  };

  const handleOpacityChange = (originalIndex, value) => {
    const model = children[originalIndex];
    if (!model) return;

    model.material.opacity = value;
    model.material.needsUpdate = true;

    const next = [...opacity];
    next[originalIndex] = value;
    setOpacity(next);

    if (value === 0 && model.visible) {
      handleVisibilityChange(originalIndex); // hide
    } else if (value > 0 && !model.visible) {
      handleVisibilityChange(originalIndex); // show
    }

    onOpacityChange?.(originalIndex, value);
  };

  const handleVisibilityChange = (originalIndex) => {
    const model = children[originalIndex];
    if (!model) return;

    const vis = [...visibility];
    const op = [...opacity];

    vis[originalIndex] = !model.visible;
    model.visible = vis[originalIndex];

    // sync opacity with visibility
    op[originalIndex] = model.visible ? 1 : 0;
    model.material.opacity = op[originalIndex];
    model.material.needsUpdate = true;

    setVisibility(vis);
    setOpacity(op);

    onVisibilityChange?.(originalIndex, vis[originalIndex]);
    onOpacityChange?.(originalIndex, op[originalIndex]);
  };

  const moveToStructure = (originalIndex) => onMoveToStructure?.(originalIndex);

  const makeOthersInvisible = (originalIndex) => {
    const vis = children.map((_, i) => i === originalIndex);
    const op = children.map((_, i) => (i === originalIndex ? 1 : 0));

    children.forEach((m, i) => {
      m.visible = vis[i];
      m.material.opacity = op[i];
      m.material.needsUpdate = true;
      onVisibilityChange?.(i, vis[i]);
      onOpacityChange?.(i, op[i]);
    });

    setVisibility(vis);
    setOpacity(op);
  };

  // Check if mobile portrait (upside down) view
  const isMobilePortrait =
    isMobile &&
    typeof window !== "undefined" &&
    window.innerWidth < window.innerHeight;

  return (
    <div
      className={`absolute ${
        isMobilePortrait ? "bottom-20 right-20" : "bottom-1 left-[17rem]"
      } w-auto md:w-1/4 flex flex-col items-start z-50`}
    >
      <div
        className="relative flex flex-col items-start space-y-3 p-3 pt-7 pb-3 bg-white rounded-lg shadow-lg border border-gray-200 w-40"
        style={{
          "--main-color": anatomyConstants.mainColor,
          "--main-color-bold": anatomyConstants.mainColorBold,
        }}
      >
        <div
          className={`flex flex-col space-y-2 overflow-y-auto ${
            isMobile ? "max-h-28" : "max-h-36"
          } direction-ltr`}
        >
          {list.map(({ mesh }, i) => (
            <label
              key={`${mesh.uuid}`}
              className="flex items-center space-x-2 w-full"
            >
              <input
                type="radio"
                name="model-select"
                checked={selectedIdxInList === i}
                onChange={() => setSelectedIdxInList(i)}
                className="accent-[var(--main-color)]"
              />
              <span className="text-sm md:text-xs text-gray-700">
                {mesh.name}
              </span>
            </label>
          ))}
          {list.length === 0 && (
            <div className="text-xs text-gray-500">No valid meshes.</div>
          )}
        </div>

        <div className="flex w-full justify-between items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (selectedIdxInList == null) return;
              const originalIndex = list[selectedIdxInList].originalIndex;
              handleCenterline(originalIndex);
            }}
            disabled={selectedIdxInList == null}
            className={`flex-1 py-2 px-3 text-sm md:text-xs font-inter font-medium text-white rounded transition ${
              selectedIdxInList != null
                ? "bg-[var(--main-color)] hover:bg-[var(--main-color-bold)] cursor-pointer border border-[#0000001A]"
                : "bg-gray-400 text-gray-200 cursor-not-allowed border border-[#0000001A]"
            }`}
          >
            Centerline
          </button>
        </div>
      </div>
    </div>
  );
};

export default CenterlineMeshSelectionModal;
